import { Send } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { cn } from '../lib/cn'
import { Button } from './button'
import { Input } from './input'
import { formatDateTime } from '../lib/format'

export type MessageThreadItem = {
  id: string
  senderId: string
  senderName: string
  body: string
  createdAt: string
}

export type MessageThreadProps = {
  messages: MessageThreadItem[]
  /** Bubbles on the right, labelled "Vous", when a message's sender matches this. */
  myId: string | null
  loading: boolean
  body: string
  onBodyChange: (value: string) => void
  onSend: () => void
  sending: boolean
  error?: string | null
}

/**
 * The mission chat (frame `7557:9070`), shared by the client's own screen and
 * the provider's Messagerie tab — the same thread, styled once.
 */
export function MessageThread({
  messages,
  myId,
  loading,
  body,
  onBodyChange,
  onSend,
  sending,
  error,
}: MessageThreadProps) {
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' })
  }, [messages.length])

  return (
    // `h-full` and not `min-h-full`: the thread fills the space it is given
    // rather than demanding a whole viewport. `min-h-full` made it 100% tall
    // *in addition to* the mission header and tab strip above it, so the page
    // overflowed by exactly their height and the composer fell off the bottom
    // — you had to scroll to reach the box you type into.
    <div className="flex h-full min-h-0 flex-col">
      {/* Only this scrolls. `min-h-0` is load-bearing: a flex child defaults to
          min-height:auto and would grow to fit every message instead of
          scrolling, pushing the composer down again. */}
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
        {loading ? (
          <p role="status" className="animate-slide-up text-center text-sm text-slate-500">
            Chargement…
          </p>
        ) : messages.length === 0 ? (
          <p className="animate-slide-up text-center text-sm text-slate-500">
            Aucun message pour le moment.
          </p>
        ) : (
          messages.map((m) => {
            const mine = m.senderId === myId
            return (
              <div key={m.id} className="flex flex-col gap-1">
                <p
                  className={cn(
                    'text-xs font-bold text-slate-700',
                    mine ? 'text-right' : 'text-left',
                  )}
                >
                  {mine ? 'Vous' : m.senderName}
                  <span className="ml-2 font-normal text-slate-500">
                    {formatDateTime(m.createdAt)}
                  </span>
                </p>
                <div className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
                  <p
                    className={cn(
                      // Each bubble rises into place. The one you just sent is
                      // the whole confirmation that it went - there is no other
                      // "sent" indicator on this screen.
                      'animate-bubble-in max-w-[253px] rounded-2xl px-3 py-3 text-sm',
                      mine ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600',
                    )}
                  >
                    {m.body}
                  </p>
                </div>
              </div>
            )
          })
        )}
        <div ref={bottomRef} />
      </div>

      {error ? (
        // Slides in rather than appearing: something the user did produced
        // this, so it should read as an answer arriving.
        <p
          role="alert"
          className="animate-slide-up mx-4 mb-2 rounded-md bg-rose-50 p-2 text-xs text-rose-700"
        >
          {error}
        </p>
      ) : null}

      {/* `shrink-0`: pinned to the bottom whatever the screen height and
          however many messages there are. */}
      <form
        className="flex shrink-0 items-center gap-2 border-t border-slate-200 bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
        onSubmit={(e) => {
          e.preventDefault()
          if (body.trim() !== '') onSend()
        }}
      >
        <div className="flex-1">
          <Input
            aria-label="Écrire un message"
            placeholder="Ecrire ici..."
            value={body}
            onChange={(e) => onBodyChange(e.target.value)}
          />
        </div>
        <Button type="submit" disabled={body.trim() === '' || sending} aria-label="Envoyer">
          <Send className="size-5" aria-hidden="true" />
        </Button>
      </form>
    </div>
  )
}
